import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import styled from "@emotion/styled";

import ParkkiTheme from "themes/ParkkiTheme";

import PedestrianStatisticsActioner from "services/pedestrianStatistics/actioner";
import PedestrianStatisticsUtils from "services/pedestrianStatistics/utils";
import Abilities from "services/permissions/abilities";
import ContractUtils from "services/contract/utils";
import useLiveReload from "services/hooks/useLiveReload";

import DateTimePicker from "components/DateTimePicker";
import SelectedLayersSection from "components/SelectedLayersSection";
import LiveButton from "components/LiveButton";
import SelectedLayersSectionButton from "components/SelectedLayersSectionButton";

import PedestrianStatisticsStatsSection from "../PedestrianStatisticsStatsSection";

const Container = styled.div`
  display: flex;
  flex: 1;
  background-color: ${ParkkiTheme.colors.lightGrey};
  flex-direction: column;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  padding-left: 20px;
  padding-right: 20px;
  margin-top: 8px;
  gap: ${ParkkiTheme.spacings.small};
`;

const FilterContainer = styled.div`
  display: flex;
  flex: 1;
`;

const SelectedAreasContainer = styled.div`
  display: flex;
  justify-self: flex-end;
`;

function PedestrianStatisticsLayout({
  contract,
  currentFilters,
  currentFloorAreas,
  onTimestampChange,
  pedestrianStatistics,
  setAutoRefresh,
  setSelectedLayers,
}) {
  const [toggleLayersSection, setToggleLayersSection] = useState(false);

  const handleDateChange = useCallback(
    (updatedDateTime) => {
      const timestamp = Math.round(updatedDateTime?.valueOf() / 1000);
      onTimestampChange(timestamp);
    },
    [onTimestampChange]
  );

  const handleReloadOnce = useCallback(() => {
    handleDateChange(new Date().getTime());
  }, [handleDateChange]);

  const handleSetAutoRefresh = useCallback(() => {
    setAutoRefresh(true);
  }, [setAutoRefresh]);

  const { liveReloadActive, onActivateLiveReload, onCancelLiveReload } =
    useLiveReload({
      key: "PEDESTRIAN_PAGE_STATS_LIVE_RELOAD_INTERVAL_ID",
      onSetAutoRefresh: handleSetAutoRefresh,
      onReload: handleReloadOnce,
    });

  useEffect(() => {
    if (!pedestrianStatistics?.map?.initialized) {
      setToggleLayersSection(false);
    }
  }, [pedestrianStatistics?.map?.initialized]);

  useEffect(() => {
    if (
      !toggleLayersSection &&
      pedestrianStatistics?.map?.initialized &&
      pedestrianStatistics?.map?.selectedLayers?.length === 0
    ) {
      setToggleLayersSection(true);
    }
  }, [
    toggleLayersSection,
    pedestrianStatistics?.map?.selectedLayers,
    pedestrianStatistics?.map?.initialized,
  ]);

  function handleAutoReloadChecked(checked) {
    const callback = checked ? onActivateLiveReload : onCancelLiveReload;
    return callback();
  }

  function shouldDisableDate(datetime) {
    const diffDays = moment()
      .startOf("day")
      .diff(datetime.clone().startOf("day"), "days");
    if (
      (diffDays > 0 &&
        diffDays > ContractUtils.getMaxDaysAllowedHeatmapOccupancy(contract)) ||
      diffDays < 0 ||
      (diffDays === 0 && !moment(datetime).isSame(new Date(), "day"))
    ) {
      return true;
    }

    return false;
  }

  return (
    <Container>
      <FiltersContainer>
        <DateTimePicker
          value={currentFilters?.timestamp * 1000}
          onChange={handleDateChange}
          shouldDisableDate={shouldDisableDate}
        />
        <LiveButton
          withLiveReload={liveReloadActive}
          onReload={handleReloadOnce}
          onChecked={handleAutoReloadChecked}
        />

        <FilterContainer />

        {Abilities.ability.can("read", "pedestrian_stats.areasSelection") && (
          <SelectedAreasContainer>
            <SelectedLayersSectionButton
              toggled={toggleLayersSection}
              onClick={() => setToggleLayersSection(!toggleLayersSection)}
            />
          </SelectedAreasContainer>
        )}
      </FiltersContainer>

      {toggleLayersSection &&
      Abilities.ability.can("read", "pedestrian_stats.areasSelection") ? (
        <SelectedLayersSection
          loading={!pedestrianStatistics?.map?.initialized}
          areas={currentFloorAreas}
          onSelectLayers={setSelectedLayers}
        />
      ) : null}

      <PedestrianStatisticsStatsSection />
    </Container>
  );
}

const mapStateToProps = (state) => ({
  contract: state.Contract.contract,
  currentFilters: state.PedestrianStatistics.filters,
  currentFloorAreas:
    PedestrianStatisticsUtils.formatAreasFromCurrentFloor(state),
  pedestrianStatistics: state.PedestrianStatistics,
});

const mapDispatchToProps = (dispatch) => ({
  setSelectedLayers: (layers) =>
    dispatch(PedestrianStatisticsActioner.setSelectedLayers(layers)),
  setAutoRefresh: (autoRefresh) =>
    dispatch(PedestrianStatisticsActioner.setAutoRefresh(autoRefresh)),
  setFilter: (key, value) =>
    dispatch(PedestrianStatisticsActioner.setFilter(key, value)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PedestrianStatisticsLayout);
